'use client'

import { PropsWithChildren, useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine'
import { draggable, dropTargetForElements } from '@atlaskit/pragmatic-drag-and-drop/element/adapter'
import { preserveOffsetOnSource } from '@atlaskit/pragmatic-drag-and-drop/element/preserve-offset-on-source'
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview'
import { cn } from '@scalis/utils'

interface DraggableProps extends PropsWithChildren {
  data?: Record<string, unknown>
}

type State = { type: 'idle' } | { type: 'preview'; container: HTMLElement } | { type: 'is-dragging-over' }

export const Draggable = (props: DraggableProps) => {
  const ref = useRef<HTMLDivElement>(null)
  const [state, setState] = useState<State>({ type: 'idle' })

  useEffect(() => {
    const el = ref.current

    if (!el) return

    return combine(
      draggable({
        element: el,
        getInitialData: () => props.data ?? {},
        onGenerateDragPreview({ nativeSetDragImage, location }) {
          setCustomNativeDragPreview({
            nativeSetDragImage,
            getOffset: preserveOffsetOnSource({ element: el, input: location.current.input }),
            render: ({ container }) => {
              container.style.width = getComputedStyle(el).width
              setState({ type: 'preview', container })
            },
          })
        },
      }),
      dropTargetForElements({
        element: el,
        canDrop: ({ source }) => source.element !== ref.current,
        getData: () => props.data ?? {},
        onDragEnter: ({ source }) => {
          if (source.element === ref.current) {
            return
          }
          setState({ type: 'is-dragging-over' })
        },
        onDragLeave: () => {
          setState({ type: 'idle' })
        },
        onDrop: () => {
          setState({ type: 'idle' })
        },
      }),
    )
  }, [props.data])

  return (
    <>
      <div
        ref={ref}
        className={cn(state.type === 'is-dragging-over' && 'opacity-50')}
      >
        {props.children}
      </div>
      {state.type === 'preview' && createPortal(props.children, state.container)}
    </>
  )
}
